import { PlusIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { useUnifiedRegistry } from "../unified-registry-context";

import useNewItemActions from "./hooks/useNewItemActions";
import { ITEM_TYPE_GROUPS, NewItemEntityType } from "./new-item-types";
import NewItemPopoverContent from "./NewItemPopoverContent";

import LoadingSpinner from "@/components/LoadingSpinner";
import SmartTableCreateDialog from "@/components/SmartTableCreateDialog";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useRbac } from "@/providers/RbacProvider";

interface NewItemButtonProps {
  currentFolderId: number | null;
  workspaceId?: number | null;
}

const NewItemButton = ({ currentFolderId, workspaceId }: NewItemButtonProps) => {
  const [open, setOpen] = useState(false);
  const { isCreateDisabled } = useRbac();

  const store = useUnifiedRegistry();
  const { actions, isLoading, loadingType, smartTableCreateDialogProps } = useNewItemActions(
    currentFolderId ?? undefined,
    store.createInNewTab,
    workspaceId,
  );

  const handleItemClick = async (type: NewItemEntityType) => {
    await actions[type]?.();
    setOpen(false);
  };

  return (
    <>
      <SmartTableCreateDialog {...smartTableCreateDialogProps} />
      <Popover
        open={open}
        onOpenChange={nextOpen => {
          if (!isLoading) {
            setOpen(nextOpen);
          }
        }}
        modal={store.modal}
      >
        <PopoverTrigger asChild>
          <Button
            className="group relative ml-auto flex items-center gap-1.5 overflow-hidden rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            size="default"
            variant="default"
            disabled={isLoading}
          >
            <span className="pointer-events-none absolute inset-0 animate-shimmer-subtle bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            {isLoading ? (
              <>
                <LoadingSpinner size={4} className="mr-1" />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <div className="flex h-5 w-5 items-center justify-center rounded bg-white/20">
                  <PlusIcon className="h-3.5 w-3.5 text-white" />
                </div>
                <span>New</span>
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 rounded-xl border border-gray-200 bg-white p-2 shadow-lg"
          align="end"
        >
          <NewItemPopoverContent
            groups={ITEM_TYPE_GROUPS}
            onItemClick={handleItemClick}
            isCreateDisabled={isCreateDisabled}
            isLoading={isLoading}
            loadingType={loadingType}
          />
        </PopoverContent>
      </Popover>
    </>
  );
};

export default observer(NewItemButton);
